import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircle2, ShieldCheck, User, AtSign, Phone, Lock, Eye, EyeOff,
  Building2, Facebook, Instagram, Upload, MapPin, Camera,
} from "@/lib/icons";
import PasswordStrength from "@/components/PasswordStrength";
import { Avatar, preparePhoto } from "@/components/PhotoUpload";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useSeo } from "@/lib/seo";
import { useSettings } from "@/lib/settings";
import { trackLead } from "@/lib/analytics";
import EmailDeliveryNotice from "@/components/EmailDeliveryNotice";

const PHOTO_ACCEPT = "image/jpeg,image/jpg,image/png,image/webp";
const PHOTO_MAX_MB = 12;

/**
 * Registration form — Full Name, Email, Mobile, Password plus a few
 * discovery-boosting details (studio name, profile photo, one social link).
 * Everything else is still collected later via `/account → Edit profile`.
 */
export default function Register() {
  const nav = useNavigate();
  const { settings } = useSettings();
  const [form, setForm] = useState({
    full_name: "", email: "", mobile: "", password: "",
    studio_name: "", category: "", facebook_url: "", instagram_url: "", pincode: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoTipOpen, setPhotoTipOpen] = useState(false);
  const photoInputRef = useRef(null);

  const categories = (settings?.categories || []).filter((c) => c && c.enabled !== false && c.name);

  useEffect(() => () => { if (photoPreview) URL.revokeObjectURL(photoPreview); }, [photoPreview]);

  const pickPhoto = (file) => {
    if (!file) return;
    if (!PHOTO_ACCEPT.split(",").includes(file.type.toLowerCase())) {
      return toast.error("Only JPG, PNG or WEBP files are allowed");
    }
    if (file.size > PHOTO_MAX_MB * 1024 * 1024) {
      return toast.error(`Photo must be ${PHOTO_MAX_MB} MB or less`);
    }
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  useSeo({
    title: "Create Your Free Photographer Account | PRONET",
    description: "Join PRONET. Create your professional profile, receive a Pro ID and get discovered across India.",
  });

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) return toast.error("Enter your full name");
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return toast.error("Enter a valid email address");
    if (form.mobile.length !== 10 || !"6789".includes(form.mobile[0])) {
      return toast.error("Enter a valid 10-digit Indian mobile number");
    }
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
    if (!photo) return toast.error("Add a profile photo");
    if (!form.studio_name.trim()) return toast.error("Enter your studio / business name");
    if (!/^\d{6}$/.test(form.pincode.trim())) return toast.error("Enter a valid 6-digit pincode");
    if (!form.facebook_url.trim() && !form.instagram_url.trim()) {
      return toast.error("Add at least one social media link (Facebook or Instagram)");
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("full_name", form.full_name.trim());
      fd.append("email", form.email.trim().toLowerCase());
      fd.append("mobile", form.mobile);
      fd.append("password", form.password);
      fd.append("studio_name", form.studio_name.trim());
      fd.append("pincode", form.pincode.trim());
      if (form.category) fd.append("category", form.category);
      if (form.facebook_url.trim()) fd.append("facebook_url", form.facebook_url.trim());
      if (form.instagram_url.trim()) fd.append("instagram_url", form.instagram_url.trim());
      // Resize/compress before sending. Production Nginx commonly rejects
      // phone-camera originals above its request-body limit before FastAPI's
      // server-side optimizer gets a chance to process them.
      const preparedPhoto = await preparePhoto(photo);
      fd.append("photo", preparedPhoto);
      const { data } = await api.post("/public/register", fd);
      setDone(data);
      trackLead("Photographer Registration");
      toast.success(`Registered! Your Member ID is ${data.member_id}`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="relative container-tight py-24 max-w-2xl">
        <div className="blobs-layer"><div className="blob blob-magenta w-[600px] h-[600px] -top-20 -left-20 opacity-40" /></div>
        <div className="relative glass rounded-3xl p-10 text-center fade-up">
          <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 shadow-[0_0_60px_-8px_rgba(16,185,129,0.6)]">
            <CheckCircle2 size={30} />
          </span>
          <h1 className="mt-6 font-display text-5xl text-white">Registration received</h1>
          <p className="mt-4 text-white/60">
            Thank you for joining PRONET. Your profile is now{" "}
            <span className="text-amber-300 font-semibold">Pending Verification</span>.
            You can now sign in and complete your profile details anytime from{" "}
            <span className="text-fuchsia-300">Profile → Edit Profile</span>.
          </p>
          <EmailDeliveryNotice className="mx-auto mt-5 max-w-md text-left" />
          <div className="mt-8 inline-block glass rounded-2xl px-8 py-5 text-left">
            <div className="overline text-white/50">Your Member ID</div>
            <div className="mt-1 font-display text-4xl text-gradient">{done.member_id}</div>
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/login" data-testid="register-done-login" className="btn-glow rounded-full px-6 py-3 text-sm font-semibold text-white">Sign in to your account</Link>
            <Link to="/gomore" data-testid="register-done-directory" className="btn-ghost rounded-full px-6 py-3 text-sm font-semibold text-white">Explore GoMore</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative container-tight py-16 md:py-20 max-w-md">
      <div className="blobs-layer">
        <div className="blob blob-violet w-[500px] h-[500px] -top-40 -left-20 opacity-40" />
        <div className="blob blob-magenta w-[380px] h-[380px] bottom-0 right-0 opacity-30" style={{ animationDelay: "-11s" }} />
      </div>

      <form onSubmit={submit} className="relative glass rounded-3xl p-6 sm:p-10 fade-up-slow">
        <div className="overline text-fuchsia-300">Join PRONET</div>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl leading-none text-white">
          Create your <span className="italic text-gradient">account</span>
        </h1>
        <p className="mt-3 text-sm text-white/60">
          Portfolio and more details can be added later from your profile.
        </p>

        <div className="mt-8 space-y-4">
          <FloatField icon={User} label="Enter Full Name" required
            testid="reg-full-name" value={form.full_name} onChange={set("full_name")} />
          <FloatField icon={AtSign} label="Enter Email Address" type="email" required
            testid="reg-email" value={form.email} onChange={set("email")}
            hint="Used for login verification and password reset." />
          <FloatField icon={Phone} label="Enter Mobile Number" required
            testid="reg-mobile" inputMode="numeric" maxLength={10}
            value={form.mobile}
            onChange={(v) => set("mobile")(v.replace(/\D/g, "").slice(0, 10))} />
          <FloatField
            icon={Lock}
            label="Enter Password"
            required
            testid="reg-password"
            type={showPw ? "text" : "password"}
            value={form.password}
            onChange={set("password")}
            hint="Minimum 8 characters."
            trailing={
              <button
                type="button"
                data-testid="reg-password-toggle"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-fuchsia-300"
                aria-label={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            }
          />
          <PasswordStrength password={form.password} testid="reg-password-strength" />
        </div>

        <div className="mt-6 pt-6 border-t border-white/10 space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative shrink-0 rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-violet-800/40 to-magenta-700/30" style={{ width: 72, height: 72 }}>
              {photoPreview ? (
                <img src={photoPreview} alt="" className="h-full w-full object-cover" />
              ) : (
                <Avatar src={null} name={form.full_name} size={72} shape="rounded" bordered={false} />
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <input ref={photoInputRef} type="file" accept={PHOTO_ACCEPT} className="hidden"
                onChange={(e) => pickPhoto(e.target.files?.[0])} data-testid="reg-photo-input" />
              <button type="button" onClick={() => (photo ? photoInputRef.current?.click() : setPhotoTipOpen(true))}
                data-testid="reg-photo-upload-btn"
                className="btn-ghost inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-white w-fit">
                <Upload size={12} /> {photo ? "Change photo" : "Add profile photo"}
                {!photo && <span className="text-fuchsia-400">*</span>}
              </button>
              <div className="text-[10px] text-white/40">A clear, well-lit headshot works best.</div>
            </div>
          </div>

          <FloatField icon={Building2} label="Studio / Business Name" required
            testid="reg-studio-name" value={form.studio_name} onChange={set("studio_name")} />

          <div>
            <label className="overline text-white/50 text-[10px]">Category (optional)</label>
            <Select value={form.category || undefined} onValueChange={set("category")}>
              <SelectTrigger data-testid="reg-category"
                className="mt-1.5 h-14 bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {categories.length
                  ? categories.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)
                  : <SelectItem value="Photographer">Photographer</SelectItem>}
              </SelectContent>
            </Select>
          </div>

          <FloatField icon={MapPin} label="Pincode" required
            testid="reg-pincode" inputMode="numeric" maxLength={6}
            value={form.pincode} onChange={(v) => set("pincode")(v.replace(/\D/g, "").slice(0, 6))}
            hint="Your city and state are detected automatically from this." />

          <div>
            <FloatField icon={Facebook} label="Facebook profile/page link"
              testid="reg-facebook" value={form.facebook_url} onChange={set("facebook_url")} />
            <div className="mt-3">
              <FloatField icon={Instagram} label="Instagram profile link"
                testid="reg-instagram" value={form.instagram_url} onChange={set("instagram_url")} />
            </div>
            <div className="mt-1.5 text-[11px] text-white/40">Add at least one link so clients can review your work.</div>
          </div>
        </div>

        <button type="submit" disabled={busy} data-testid="reg-submit"
          className="mt-8 btn-glow w-full h-12 rounded-full text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
          <ShieldCheck size={15} /> {busy ? "Submitting…" : "Create account"}
        </button>
        <p className="mt-4 text-[11px] text-white/40 text-center">
          Already registered?{" "}
          <Link to="/login" className="text-fuchsia-300 hover:underline">Sign in</Link>
        </p>
      </form>

      <Dialog open={photoTipOpen} onOpenChange={setPhotoTipOpen}>
        <DialogContent className="bg-ink-900 border-white/10 text-white">
          <DialogHeader>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-fuchsia-500/10 border border-fuchsia-500/30">
              <Camera size={20} className="text-fuchsia-300" />
            </div>
            <DialogTitle className="text-center font-display text-2xl text-white mt-2">Use a professional photo</DialogTitle>
            <DialogDescription className="text-center text-white/60">
              Clients will see this photo on your public profile and PRONET ID card.
              A clear, well-lit headshot with a plain background and your face clearly visible works best.
              Avoid group photos, heavy filters, or low-light selfies.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <button
              type="button"
              data-testid="reg-photo-tip-continue"
              onClick={() => { setPhotoTipOpen(false); photoInputRef.current?.click(); }}
              className="btn-glow w-full sm:w-auto rounded-full px-6 py-2.5 text-sm font-semibold text-white"
            >
              Choose photo
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Input with a floating label INSIDE the input bar (Material-style). */
function FloatField({ icon: Icon, label, value, onChange, testid, type = "text", hint, required, trailing, ...rest }) {
  const [focused, setFocused] = useState(false);
  const floated = focused || Boolean(value);
  return (
    <div>
      <div className="relative">
        {Icon && <Icon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />}
        <input
          data-testid={testid}
          type={type}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className={`peer w-full h-14 rounded-md bg-white/5 border border-white/10 text-white ${Icon ? "pl-9" : "pl-4"} ${trailing ? "pr-10" : "pr-4"} pt-5 pb-1 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-500/50 focus:border-fuchsia-400/40 transition-colors`}
          {...rest}
        />
        <label
          className={`pointer-events-none absolute ${Icon ? "left-9" : "left-4"} transition-all duration-200 ${
            floated
              ? "top-1.5 text-[10px] uppercase tracking-widest text-fuchsia-300"
              : "top-1/2 -translate-y-1/2 text-sm text-white/45"
          }`}
        >
          {label}{required && floated && <span className="text-fuchsia-400 ml-0.5">*</span>}
        </label>
        {trailing}
      </div>
      {hint && <div className="mt-1.5 text-[11px] text-white/40">{hint}</div>}
    </div>
  );
}
